/**
 * Integrytas Group - Base App (MOBILE)
 * Page: ODS.CONFLICTS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/OdsConflictsPage.tsx. The Offline
 * Data Sync reconciliation inbox: OPEN conflicts parked by the dispatcher
 * (USER_RECONCILE / FIELD_MERGE collisions), each carrying base / client / server
 * snapshots and the colliding field set.
 *
 * A DISTINCT presentation (dense card list + strategy facet chips + bottom-sheet
 * detail with a per-field base/client/server comparison) over the SAME data/logic
 * layer only:
 *   - reads via apiDb (the same ods_conflict projection the desktop uses),
 *   - resolution routes through the module's own sanctioned routine
 *     ods_resolve_conflict (CLIENT | SERVER | MERGED) — the desktop's write path,
 *     mirrored exactly like MobileGtcTrashPage mirrors gtc_restore. RLS still
 *     applies as the authenticated user through the gateway.
 * It never re-renders the desktop grid; §16. record_id / entity_name are the
 * audited row's own natural identifiers, so no FK resolution applies (§17.B4).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, AlertTriangle, X, GitMerge } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

interface ConflictRow {
  ods_conflict_id: number;
  entity_name: string;
  record_id: string;
  strategy: string | null;
  base_values: Record<string, unknown> | null;
  client_values: Record<string, unknown> | null;
  server_values: Record<string, unknown> | null;
  conflicting_fields: string[];
  created_at: string;
}

type RawConflict = Omit<ConflictRow, 'conflicting_fields'> & { conflicting_fields: string[] | null };

interface ResolveResult { ok: boolean; error_code?: string }

type Resolution = 'CLIENT' | 'SERVER' | 'MERGED';

// §17.B11: CHECK-enum labels go through i18n via a labelled constant — never a
// bare stored literal in the JSX. The stored value stays the code; only the label
// is localized (falling back to the code for any strategy not enumerated here).
const STRATEGY_LABELS: { value: string; fallback: string }[] = [
  { value: 'USER_RECONCILE', fallback: 'User Reconcile' },
  { value: 'FIELD_MERGE', fallback: 'Field Merge' },
];

const PAGE_CODE = 'ODS.CONFLICTS';

export function MobileOdsConflictsPage({ session }: MobilePageComponentProps) {
  // §6/§17: releasing another user's lock / restoring or resolving shared
  // state is page-scoped, so the affordance follows csm_role_page_access.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<ConflictRow[]>([]);
  const [filter, setFilter] = useState('');
  const [strategyFacet, setStrategyFacet] = useState<string>('all');
  const [selected, setSelected] = useState<ConflictRow | null>(null);
  const [mergeText, setMergeText] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('ods_conflict')
        .select('ods_conflict_id, entity_name, record_id, strategy, base_values, client_values, server_values, conflicting_fields, created_at')
        .eq('status', 'OPEN')
        .order('created_at', { ascending: false });
      if (e) throw e;
      const list: ConflictRow[] = ((data ?? []) as RawConflict[]).map((r) => ({
        ...r,
        conflicting_fields: r.conflicting_fields ?? [],
      }));
      setRows(list);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount only; entity_name / record_id are raw identifiers (no _tl to
  // follow the locale), and dates are formatted per-locale at render time.
  useEffect(() => { void reload(); }, []);

  // Reset the merged-values buffer and any stale error whenever a different
  // conflict is opened (single sheet open at a time on mobile).
  useEffect(() => {
    setMergeText('');
    setError(null);
  }, [selected?.ods_conflict_id]);

  const strategyLabel = (code: string | null): string => {
    if (!code) return t('common.none', 'None');
    const known = STRATEGY_LABELS.find((s) => s.value === code);
    return t('mobile.ods_conflicts.strategy.' + code, known?.fallback ?? code);
  };

  // Distinct strategy chips derived from the loaded rows (mirrors the desktop
  // strategy facet, presented as touch chips).
  const strategyChips = useMemo(() => {
    const distinct = [...new Set(rows.map((r) => r.strategy).filter((s): s is string => Boolean(s)))].sort();
    return [{ key: 'all', label: t('mobile.ods_conflicts.all', 'All') },
      ...distinct.map((s) => ({ key: s, label: strategyLabel(s) }))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((c) => {
      if (strategyFacet !== 'all' && (c.strategy ?? '') !== strategyFacet) return false;
      if (!q) return true;
      return (
        c.entity_name.toLowerCase().includes(q) ||
        c.record_id.toLowerCase().includes(q) ||
        (c.strategy ?? '').toLowerCase().includes(q) ||
        c.conflicting_fields.some((f) => f.toLowerCase().includes(q))
      );
    });
  }, [rows, filter, strategyFacet]);

  const collisionCount = (c: ConflictRow) => c.conflicting_fields.length;
  const collisionLabel = (n: number) =>
    t('mobile.ods_conflicts.fields_count', { count: n, defaultValue: `${n} field(s)` });

  // The fields worth showing side-by-side: the union of client-side keys and the
  // flagged colliding set (mirrors the desktop field table).
  const compareFields = (c: ConflictRow): string[] =>
    Array.from(new Set([...Object.keys(c.client_values ?? {}), ...c.conflicting_fields]));

  const showValue = (bag: Record<string, unknown> | null, field: string): string => {
    const v = bag?.[field];
    return v === undefined ? '—' : JSON.stringify(v);
  };

  const resolve = async (resolution: Resolution) => {
    if (!selected) return;
    setActionBusy(true);
    setError(null);
    try {
      let values: unknown = null;
      if (resolution === 'MERGED') {
        try {
          values = JSON.parse(mergeText.trim() || '{}');
        } catch {
          setError(t('mobile.ods_conflicts.err_merged_json', 'Merged values must be valid JSON.'));
          setActionBusy(false);
          return;
        }
      }
      // The module's sanctioned resolution routine — the desktop's write path,
      // not a direct table write. RLS applies as the authenticated user.
      const { data, error: e } = await apiDb.rpc('ods_resolve_conflict', {
        p_conflict_id: selected.ods_conflict_id,
        p_resolution: resolution,
        p_values: values,
      });
      if (e) throw e;
      const res = data as ResolveResult | null;
      if (!res?.ok) throw new Error(res?.error_code ?? 'ODS.RESOLVE_FAILED');
      setSelected(null);
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.ods_conflicts.title', 'Sync Conflicts')}
        description={t('mobile.ods_conflicts.description', 'Offline edits that collided with the server and need reconciling.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.ods_conflicts.search', 'Search conflicts')}
          aria-label={t('mobile.ods_conflicts.search', 'Search conflicts')}
        />
      </div>

      <MobileFilterChips
        chips={strategyChips.map((c) => ({ value: c.key, label: c.label }))}
        isActive={(v) => strategyFacet === v}
        onSelect={(v) => setStrategyFacet(v)}
      />

      {error && !selected && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.ods_conflicts.none', 'No open conflicts')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((c) => {
            const n = collisionCount(c);
            return (
              <MobileCardRow
                key={c.ods_conflict_id}
                lead={<AlertTriangle />}
                leadTone={n > 0 ? 'amber' : 'slate'}
                title={c.entity_name}
                subtitle={`${c.record_id} · ${strategyLabel(c.strategy)}`}
                trailing={<MobileStatusPill tone={n > 0 ? 'wait' : 'info'}>{collisionLabel(n)}</MobileStatusPill>}
                chevron
                onClick={() => setSelected(c)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.ods_conflicts.detail', 'Conflict detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><AlertTriangle size={20} /></span>
              <span>
                <div className="m-nm">{selected.entity_name}</div>
                <div className="m-em">{selected.record_id}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow label={t('mobile.ods_conflicts.strategy_k', 'Strategy')} value={strategyLabel(selected.strategy)} />
              <MobileDetailRow label={t('mobile.ods_conflicts.raised', 'Raised')} value={formatDateTime(selected.created_at)} />
              <MobileDetailRow
                label={t('mobile.ods_conflicts.colliding', 'Colliding')}
                value={
                  selected.conflicting_fields.length > 0
                    ? <MobileStatusPill tone="wait">{selected.conflicting_fields.join(', ')}</MobileStatusPill>
                    : t('common.none', 'None')
                }
              />
            </MobileContentCard>

            {/* Per-field base / client / server comparison — the substance of the
                conflict, laid out as read-only rows for a phone. */}
            <div className="m-fieldcard">
              {compareFields(selected).length === 0 ? (
                <div className="m-formrow">
                  <div className="m-formrow-ro">{t('mobile.ods_conflicts.no_values', 'No field snapshots recorded.')}</div>
                </div>
              ) : (
                compareFields(selected).map((f) => {
                  const collides = selected.conflicting_fields.includes(f);
                  return (
                    <div key={f} className="m-formrow">
                      <label>
                        {f}
                        {collides && <span className="m-formrow-hint"> · {t('mobile.ods_conflicts.collision', 'collision')}</span>}
                      </label>
                      <div className="m-formrow-ro">
                        <div>{t('mobile.ods_conflicts.base', 'Base')}: {showValue(selected.base_values, f)}</div>
                        <div>{t('mobile.ods_conflicts.client', 'Client')}: {showValue(selected.client_values, f)}</div>
                        <div>{t('mobile.ods_conflicts.server', 'Server')}: {showValue(selected.server_values, f)}</div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label htmlFor="m-ods-merge">{t('mobile.ods_conflicts.merged_values', 'Merged values (JSON)')}</label>
                <input
                  id="m-ods-merge"
                  className="m-inp"
                  value={mergeText}
                  onChange={(e) => setMergeText(e.target.value)}
                  placeholder={t('mobile.ods_conflicts.merge_placeholder', 'e.g. {"field":"value"}')}
                />
                <span className="m-formrow-hint">{t('mobile.ods_conflicts.merge_hint', 'Used only by Apply Merged.')}</span>
              </div>
            </div>

            {error && <MobileContentCard><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></MobileContentCard>}

            <div className="m-actionbar">
              {canChange && (<button type="button" className="m-btn m-btn-primary" disabled={actionBusy} onClick={() => void resolve('MERGED')}>
                <GitMerge size={16} />
                {t('mobile.ods_conflicts.apply_merged', 'Apply Merged')}
              </button>)}
              {canChange && (<button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => void resolve('CLIENT')}>
                {t('mobile.ods_conflicts.keep_client', 'Keep Client')}
              </button>)}
              {canChange && (<button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => void resolve('SERVER')}>
                {t('mobile.ods_conflicts.keep_server', 'Keep Server')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
